import { type FC } from "react";

import {
  computeStepHitPct,
  fmtHitPct,
  fmtTime,
  fmtTokens,
  formatStepErrorLabel,
  type StepEntity,
} from "./observabilityModel";
import { WakeDurationCell } from "./WakeRow";

export const StepOutputCell: FC<{ summary: StepEntity["outputSummary"] }> = ({
  summary,
}) => {
  if (!summary) {
    return <span className="min-w-0 flex-1" />;
  }
  const { kind, toolNames, textPreview } = summary;
  if (kind === "delivered_tool" || kind === "tool_work") {
    const primary = toolNames[0] ?? "tool";
    const extra = toolNames.length > 1 ? ` +${toolNames.length - 1}` : "";
    const cls =
      kind === "delivered_tool"
        ? "text-emerald-600 dark:text-emerald-400"
        : "text-sky-600 dark:text-sky-400";
    return (
      <span
        className={`min-w-0 flex-1 truncate ${cls}`}
        title={toolNames.join(", ")}
      >
        ⚒ {primary}
        {extra}
      </span>
    );
  }
  if (kind === "void_text") {
    return (
      <span
        className="min-w-0 flex-1 truncate text-amber-600 dark:text-amber-400"
        title={textPreview ?? "assistant text, never delivered"}
      >
        ✎ text{textPreview ? `: ${textPreview}` : ""}
      </span>
    );
  }
  return (
    <span
      className="min-w-0 flex-1 truncate text-slate-300 dark:text-slate-600"
      title="no tool call or text (reasoning-only / empty)"
    >
      —
    </span>
  );
};

export const StepList: FC<{
  steps: StepEntity[];
  onOpenStep: (stepId: string) => void;
}> = ({ steps, onOpenStep }) => {
  if (steps.length === 0) {
    return (
      <div className="py-2 font-mono text-[10px] text-slate-400">no steps</div>
    );
  }
  return (
    <div className="divide-y divide-slate-100 dark:divide-slate-800">
      {steps.map((step) => {
        const hitPct = computeStepHitPct(step);
        return (
          <button
            key={step.id}
            type="button"
            onClick={() => onOpenStep(step.id)}
            className="flex w-full min-w-0 items-center gap-2 px-1 py-1 text-left font-mono text-[10px] transition-colors hover:bg-white dark:hover:bg-slate-950"
          >
            <span className="w-6 shrink-0 text-slate-400">
              #{step.stepIndexInWake}
            </span>
            <span className="w-12 shrink-0 text-slate-400">
              {fmtTime(step.startedAt)}
            </span>
            <span className="w-12 shrink-0 text-right text-slate-600 dark:text-slate-300">
              {fmtTokens(step.inputTokens)}
            </span>
            <span className="w-12 shrink-0 text-right text-emerald-600 dark:text-emerald-400">
              {fmtHitPct(hitPct)}
            </span>
            <span className="w-12 shrink-0 text-right text-slate-400">
              {fmtTokens(step.outputTokens)}o
            </span>
            <WakeDurationCell
              turnMs={step.durationMs}
              lockWaitMs={step.lockWaitMs}
            />
            <StepOutputCell summary={step.outputSummary} />
            <span className="shrink-0 truncate text-right text-slate-400">
              {step.finishReason ?? ""}
              {step.errorKind ? ` · ${formatStepErrorLabel(step)}` : ""}
            </span>
          </button>
        );
      })}
    </div>
  );
};
